import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "../../hooks/hooks";
import FixedProductCard from "./PaymentCard";
import { PlanListsProps } from "./types";
import { convertCurrencyAmount, formatCurrency } from "../../utils/utils";

const PlanLists = (props: PlanListsProps) => {
  const { data: appData } = useAppSelector((state) => state.appData);
  const {
    selectedCurrency,
    currency: { items: localeCurrencies },
  } = useAppSelector((state) => state.locale);
  const { user } = useAppSelector((state) => state.auth);

  const location = useLocation();
  const path = location.pathname;
  const segments = path.split("/").filter(Boolean);
  const formattedProduct = props.productType;
  const formattedProvider =
    segments.length >= 1 ? segments[segments.length - 1] : "";

  const productSection = appData[formattedProduct];
  const providers = Array.isArray(productSection?.providers)
    ? productSection.providers
    : [];
  const categories = Array.isArray(productSection?.categories)
    ? productSection.categories
    : [];
  const productPlans = Array.isArray(productSection?.plans)
    ? productSection.plans
    : [];

  const provider = providers.find(
    (item: { slug: string }) => item.slug === formattedProvider,
  );

  const payButtonRef = useRef<HTMLButtonElement>(null);
  const filteredCategories = categories.filter(
    (category: any) => category.provider_id === provider?.id,
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(0);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [validationError, setValidationError] = useState<{
    field: "unique_number" | "additional" | "none";
    message: string | null;
  }>({ field: "none", message: null });
  const [verifyMessage, setVerifyMessage] = useState("");
  const [isVerifyingRecipient, setIsVerifyingRecipient] = useState(false);

  const [uniqueNumber, setUniqueNumber] = useState("");
  const [additionalInputValue, setAdditionalInputValue] = useState("");
  const apiUrl = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    if (filteredCategories.length > 0) {
      setSelectedCategoryId(filteredCategories[0].id);
      setSelectedPlan(null);
      return;
    }
    setSelectedCategoryId(0);
    setSelectedPlan(null);
  }, [provider?.id, filteredCategories.length]);
  // Filter plans based on the selected category
  const plans = productPlans.filter(
    (plan: any) => plan.category_id === selectedCategoryId,
  );

  const handlePlanClick = (plan: any) => {
    setSelectedPlan(plan);
    setTimeout(() => {
      if (payButtonRef.current) {
        payButtonRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 200);
  };

  const currencies = localeCurrencies || appData.currencies || [];
  const getCurrencyById = (currencyId?: number) =>
    currencies.find((currency: any) => currency.id === currencyId);
  const dgCoinCurrency = currencies.find(
    (currency: any) => currency.symbol === "Coins",
  );

  const formatConvertedPrice = (
    amount: number | string,
    currencyId?: number,
  ) => {
    const fromCurrency =
      getCurrencyById(currencyId) || selectedCurrency || currencies[0];
    const toCurrency = selectedCurrency || fromCurrency;
    const numericAmount = Number(amount);
    const converted = convertCurrencyAmount(
      numericAmount,
      fromCurrency,
      toCurrency,
    );
    return {
      symbol: toCurrency?.symbol || "",
      value: formatCurrency(converted),
    };
  };

  const formatDGCoinPrice = (amount: number | string, currencyId?: number) => {
    const fromCurrency = getCurrencyById(currencyId) || dgCoinCurrency;
    if (!fromCurrency || !dgCoinCurrency) return formatCurrency(Number(amount));
    const numericAmount = Number(amount);
    const converted = convertCurrencyAmount(
      numericAmount,
      fromCurrency,
      dgCoinCurrency,
    );
    return formatCurrency(converted);
  };

  const recipientLabel = provider?.recipient_label || "Recipient";
  const additionalInputEnabled = Boolean(provider?.additional_input);
  const additionalInputLabel =
    provider?.additional_input_label || "Additional Details";
  const additionalInputType = provider?.additional_input_type || "text";
  const additionalInputPlaceholder =
    provider?.additional_input_placeholder || "";
  const additionalInputName =
    provider?.additional_input_form_name || "additional_input";
  const shouldVerifyRecipient = Boolean(provider?.verify_recipient);

  const purchaseEndpointMap: Record<string, string> = {
    airtime: "airtime-purchase",
    data: "data-purchase",
    electric: "electric-purchase",
    cable: "cable-purchase",
    gift_cards: "gift-cards-purchase",
    game_cards: "game-cards-purchase",
    topup: "top-ups-purchase",
    dg_coins: "dgcoin-purchase",
  };

  const verifyEndpoint = selectedPlan?.id
    ? `${apiUrl}/${purchaseEndpointMap[props.productType]}/verify/${selectedPlan.id}/${encodeURIComponent(uniqueNumber.trim())}`
    : "";

  const extractVerifyMessage = (payload: any) =>
    payload?.message ||
    payload?.data?.message ||
    payload?.error ||
    "Unable to verify recipient.";

  const handleAdditionalInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const nextValue = event.target.value;
    if (validationError.field === "additional") {
      setValidationError({ field: "none", message: null });
    }
    if (additionalInputType === "number") {
      setAdditionalInputValue(nextValue.replace(/[^0-9]/g, ""));
      return;
    }
    setAdditionalInputValue(nextValue);
  };

  const handleVerifyRecipient = async () => {
    if (!shouldVerifyRecipient) return;
    setVerifyMessage("");
    const trimmedUniqueNumber = uniqueNumber.trim();
    if (!trimmedUniqueNumber) {
      setValidationError({
        field: "unique_number",
        message: `${recipientLabel} is required.`,
      });
      return;
    }
    if (!selectedPlan?.id) {
      setVerifyMessage("Select a plan before verifying recipient.");
      return;
    }
    setValidationError({ field: "none", message: null });
    setIsVerifyingRecipient(true);
    try {
      const response = await axios.get(verifyEndpoint);
      setVerifyMessage(extractVerifyMessage(response.data));
    } catch (error: any) {
      setVerifyMessage(
        extractVerifyMessage(error?.response?.data) ||
          "Unable to verify recipient.",
      );
    } finally {
      setIsVerifyingRecipient(false);
    }
  };

  useEffect(() => {
    setVerifyMessage("");
  }, [provider?.id, selectedPlan?.id]);

  return (
    <div className="rounded-lg border border-border-border1 dark:border-tabs w-full lg:w-[660px] overflow-hidden">
      {/* Header */}
      <div className="w-full dark:bg-[#FFFFFF1A] border-b border-border-1 dark:border-tabs">
        <p className="font-semibold dark:text-white p-4 text-sm lg:text-base">
          Products
        </p>
      </div>

      {/* Categories Filter - Fixed scrolling and padding */}
      {filteredCategories.length > 1 && (
        <div className="px-4 mt-4">
          <div className="flex overflow-x-auto gap-2 p-1 bg-[#F4F4F5] dark:bg-BG-dark rounded-lg no-scrollbar">
            {filteredCategories.map((item: any, index: number) => (
              <button
                key={index}
                className={`flex-none py-2 px-6 rounded-lg text-xs transition-all whitespace-nowrap ${
                  selectedCategoryId === item.id
                    ? "inter-xs-semibold-dark dark:text-white bg-[#FCFCFD] dark:bg-[#FFFFFF1A] shadow-sm"
                    : "inter-xs-medium-toggleBG-dark dark:text-main-dark"
                }`}
                onClick={() => {
                  setSelectedCategoryId(item.id);
                  setSelectedPlan(null);
                }}
              >
                {item.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Plan Grid - Improved spacing */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
        {plans.map((plan: any, index: number) => {
          const hasDiscount =
            plan.discount > 0 && plan.discount > plan.wallet_price;
          const discountPercent = hasDiscount
            ? ((plan.discount - plan.wallet_price) / plan.discount) * 100
            : 0;
          const { symbol, value } = formatConvertedPrice(
            plan.wallet_price,
            plan.price_currency_id,
          );
          const dgCoinValue = formatDGCoinPrice(
            plan.wallet_price,
            plan.price_currency_id,
          );
          const convertedOriginal = hasDiscount
            ? formatConvertedPrice(plan.discount, plan.price_currency_id)
            : null;

          return (
            <div
              key={index}
              className={`flex items-center justify-between border p-3 dark:bg-[#FFFFFF1A] dark:border-tabs rounded-xl w-full cursor-pointer transition-all ${
                selectedPlan?.id === plan.id
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500"
                  : "bg-transparent"
              }`}
              onClick={() => handlePlanClick(plan)}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <img
                  src={
                    plan.image_url ||
                    plan.category.provider?.image_url ||
                    plan.provider?.image_url ||
                    ""
                  }
                  alt="img"
                  className="w-10 h-10 bp:w-12 bp:h-12 shrink-0 rounded-lg object-cover"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-1">
                    <p className="text-xs bp:text-sm font-bold truncate dark:text-white">
                      {plan.name}
                    </p>
                    {hasDiscount && (
                      <span className="bg-[#FEF3F2] px-1.5 py-0.5 rounded-full shrink-0">
                        <p className="text-[#D92D20] text-[10px] font-semibold">
                          {Math.round(discountPercent)}% off
                        </p>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    {plan.price_type === "fixed" && (
                      <div className="bg-[#039EF2] text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                        <img
                          src="/png/dg.png"
                          alt="C"
                          className="w-3 h-3 object-contain"
                        />
                        <span className="text-[10px] font-semibold">
                          {dgCoinValue}
                        </span>
                      </div>
                    )}
                    <div className="flex items-baseline gap-1">
                      <p className="text-black text-[10px] bp:text-xs dark:text-white font-medium">
                        {symbol}
                        {value}
                      </p>
                      {convertedOriginal && (
                        <p className="text-gray-400 text-[9px] bp:text-[10px] line-through">
                          {convertedOriginal.symbol}
                          {convertedOriginal.value}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recipient Details Section */}
      <div className="px-4 pb-4">
        {selectedPlan ? (
          props.productType !== "dg_coins" && (
            <div className="mt-2 border border-border-border1 dark:border-tabs rounded-xl p-4 dark:bg-[#FFFFFF1A]">
              <div className="flex items-center justify-between gap-2 mb-6">
                <p className="text-sm bp:text-base font-semibold text-black dark:text-white">
                  {recipientLabel}
                </p>
                {["game_cards", "gift_cards", "airtime", "data"].includes(
                  props.productType,
                ) && (
                  <button
                    type="button"
                    className="px-4 py-2 text-xs font-semibold text-white rounded-lg bg-gradient-to-b from-[#039EF2] to-[#027EC2]"
                    onClick={() => {
                      const selfValue = ["airtime", "data"].includes(
                        props.productType,
                      )
                        ? user?.phone_number
                        : user?.email;
                      if (selfValue) {
                        setUniqueNumber(selfValue);
                        setVerifyMessage("");
                        setValidationError({ field: "none", message: null });
                      }
                    }}
                  >
                    Buy for self
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="text"
                  name="unique_number"
                  placeholder={recipientLabel}
                  className="flex-1 p-3 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                  value={uniqueNumber}
                  onChange={(e) => {
                    setValidationError({ field: "none", message: null });
                    setVerifyMessage("");
                    setUniqueNumber(e.target.value);
                  }}
                />
                {shouldVerifyRecipient && (
                  <button
                    type="button"
                    className="px-5 py-3 text-xs font-semibold text-white rounded-lg bg-gradient-to-b from-[#039EF2] to-[#027EC2] disabled:opacity-50"
                    onClick={handleVerifyRecipient}
                    disabled={isVerifyingRecipient || !uniqueNumber.trim()}
                  >
                    {isVerifyingRecipient ? "..." : "Verify"}
                  </button>
                )}
              </div>

              {validationError.field === "unique_number" && (
                <p className="mt-1.5 text-[11px] text-red-500">
                  {validationError.message}
                </p>
              )}
              {shouldVerifyRecipient && verifyMessage && (
                <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                  {verifyMessage}
                </p>
              )}

              {additionalInputEnabled && (
                <div className="mt-6">
                  <p className="text-sm font-semibold text-black dark:text-white mb-2">
                    {additionalInputLabel}
                  </p>
                  <input
                    name={additionalInputName}
                    type={additionalInputType}
                    placeholder={additionalInputPlaceholder}
                    className="w-full p-3 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-white outline-none"
                    value={additionalInputValue}
                    onChange={handleAdditionalInputChange}
                  />
                  {validationError.field === "additional" && (
                    <p className="mt-1.5 text-[11px] text-red-500">
                      {validationError.message}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-6 bg-[#E6F5FE] dark:bg-blue-900/30 rounded-xl p-4 flex items-center gap-3">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-[#039EF2]"
                  id="save-details"
                />
                <label
                  htmlFor="save-details"
                  className="text-xs text-gray-600 dark:text-gray-300"
                >
                  Save my details for next time.
                </label>
              </div>
            </div>
          )
        ) : (
          <div className="py-10 text-center">
            <p className="text-sm font-medium text-gray-400">
              Select a product!
            </p>
          </div>
        )}

        <div className="mt-4">
          <FixedProductCard
            ref={payButtonRef}
            selectedPlan={selectedPlan}
            unique_number={uniqueNumber}
            type={props.productType}
            additionalInputValue={additionalInputValue}
            recipientLabel={recipientLabel}
            additionalInputLabel={additionalInputLabel}
            requiresRecipient={props.productType !== "dg_coins"}
            requiresAdditionalInput={additionalInputEnabled}
            onValidationError={setValidationError}
          />
        </div>
      </div>
    </div>
  );
};

export default PlanLists;
